import classnames from 'classnames';
import React from 'react';
import { partOfSpeech } from 'es6/helpers/localization';
import { t } from 'es6/services/localization';
import MouseoverDictionary from 'iknow-mouseover-dictionary/react';
import ProgressBar from 'es6/components/common/progress-bar';
import SoundManager from 'es6/services/sound-manager';
import { TranscodedImageSizes, TranscodedImage } from 'es6/components/common/transcoded-image';
import * as paths from 'es6/core/paths';
import AddToCoursesModal from 'es6/components/courses/add-to-courses-modal';
import BaseModal from 'es6/components/common/modals/base';
import Config from 'es6/core/config';

// The values are used as the strings for Localization.
const SortBy = {
  Default: t('default'),
  PartOfSpeech: t('sort.part of speech'),
  Cue: t('sort.cue'),
  Response: t('response'),
};

// The values are used as the strings for Localization.
const BulkActions = {
  SkipItems: t('skip items'),
  UnskipItems: t('unskip'),
  SetToComplete: t('master items'),
  ResetItems: t('reset items'),
  DeferItems: t('defer items'),
  AddItemsToCourse: t('add items to course'),
};

export default class CourseContent extends React.Component {
  constructor(props) {
    super(props);

    // Set initial state.
    this.state = {
      selectedBulkAction: BulkActions.SkipItems,
      filterBy: '',
      selectAllChecked: false,
      sortBy: SortBy.Default,
    };

    // Actions for AddToCoursesModal.
    this._closeAddToCoursesModal = () => {
      this.setState({ isAddToCoursesModalOpen: false });
    };
    this._openAddToCoursesModal = () => {
      this.setState({ isAddToCoursesModalOpen: true });
    };
    this._submitAddToCoursesModal = (customGoalId) => {
      this.props.onAddToCourse(this._getItemIds(), customGoalId);
      this._closeAddToCoursesModal();
    };
  }

  componentDidMount() {
    this._setupDictionary();
  }

  /**
   * Whenever the component receives new memories or item packages,
   * uncheck everything.
   */
  componentWillReceiveProps(nextProps) {
    if (nextProps.itemPackages || nextProps.memories) {
      this._toggleCheckboxes(false);
      this.setState({ selectAllChecked: false });
    }
  }

  componentDidUpdate() {
    this._setupDictionary();
  }

  /**
   * Callback to handle the audio icon being clicked.
   *
   * @param {String} sound
   */
  _handleAudioClick(sound) {
    SoundManager.play(sound);
  }

  /**
   * Callback for when the apply button is clicked. Get
   * the selected item IDs, the action and call the appropriate
   * callback method.
   */
  _handleBulkAction() {
    // Don't send any actions up while isLoading.
    if (this.props.isLoading) {
      return;
    }

    const itemIds = this._getItemIds();

    switch (this.state.selectedBulkAction) {
      case BulkActions.SkipItems:
        this.props.onSkip(itemIds);
        break;
      case BulkActions.UnskipItems:
        this.props.onUnskip(itemIds);
        break;
      case BulkActions.SetToComplete:
        this.props.onSetToComplete(itemIds);
        break;
      case BulkActions.ResetItems:
        this.props.onReset(itemIds);
        break;
      case BulkActions.DeferItems:
        this.props.onDefer(itemIds);
        break;
      case BulkActions.AddItemsToCourse:
        // If there are no itemIds, don't do anything.
        if (itemIds.length > 0) {
          // Just show the modal. The actual this.props.onAddToCourse callback will be called once the
          // button in the modal is pressed.
          this._openAddToCoursesModal();
        }
        break;
      default:
        break;
    }
  }

  _getItemIds() {
    const checkboxes = [...this.refs.items.querySelectorAll('input:checked')];
    return checkboxes.map(checkbox => parseInt(checkbox.value, 10));
  }

  /**
   * Callback for when the bulk action changes.
   *
   * @param {e} SyntheticEvent
   */
  _handleBulkActionChange(e) {
    this.setState({ selectedBulkAction: e.target.value });
  }

  /**
   * Callback for when the filter changes.
   *
   * @param {e} SyntheticEvent
   */
  _handleFilterByChange(e) {
    this.setState({ filterBy: e.target.value });
  }

  /**
   * Toggles state for whether everything is checked or not. Updates
   * the selected items.
   *
   * @param {e} SyntheticEvent
   */
  _handleSelectAllClick(e) {
    const isChecked = !this.state.selectAllChecked;
    this._toggleCheckboxes(isChecked);
    this.setState({ selectAllChecked: isChecked });
  }

  /**
   * Callback for when the sort by changes.
   *
   * @param {e} SyntheticEvent
   */
  _handleSortByChange(e) {
    this.setState({ sortBy: e.target.value });
  }

  /**
   * Binds the mouseover dictionary words to the elements on
   * the page.
   */
  _setupDictionary() {
    const mouseoverDictionary = this.props.mouseoverDictionary;

    if (mouseoverDictionary) {
      mouseoverDictionary.then(() => {
        mouseoverDictionary.bind(this.refs.courseContent);
      });
    }
  }

  /**
   * Toggles the tags on all the checkboxes.
   *
   * @param {Boolean} isChecked
   */
  _toggleCheckboxes(isChecked) {
    const checkboxes = [...this.refs.items.querySelectorAll('input[type=checkbox]')];
    checkboxes.forEach(checkbox => {
      const box = checkbox;
      box.checked = isChecked;
    });
  }

  _renderBulkActions() {
    if (!this.props.enableBulkActions) {
      return null;
    }

    return (
      <div className="bulk-actions">
        <input className="checkbox" type="checkbox" checked={this.state.selectAllChecked} onChange={(...args) => this._handleSelectAllClick(...args)} />
        {' '}
        <select name="bulk-actions" value={this.state.selectedBulkAction} onChange={(...args) => this._handleBulkActionChange(...args)} >
          <option value={BulkActions.SkipItems}>{BulkActions.SkipItems}</option>
          <option value={BulkActions.UnskipItems}>{BulkActions.UnskipItems}</option>
          <option value={BulkActions.SetToComplete}>{BulkActions.SetToComplete}</option>
          <option value={BulkActions.DeferItems}>{BulkActions.DeferItems}</option>
          {
            Config.user.groupRestrictions.canResetItemProgress && (
              <option value={BulkActions.ResetItems}>{BulkActions.ResetItems}</option>
            )
          }

          {
            this.props.isContentCreationEnabled && (
              <option value={BulkActions.AddItemsToCourse}>{BulkActions.AddItemsToCourse}</option>
            )
          }
        </select>
        {' '}
        <input type="button" className="apply" value={t('apply')} onClick={(...args) => this._handleBulkAction(...args)} />
      </div>
    );
  }

  _renderItems() {
    const itemPackages = this.props.itemPackages

      // Filter items.
      .filter(itemPackage => {
        const strings = [
          itemPackage.getText(),
          itemPackage.getTranslation(),
          itemPackage.getTransliteration(),
        ];

        // If there is a match, return true.
        for (let i = 0; i < strings.length; i++) {
          if (strings[i] && strings[i].toUpperCase().indexOf(this.state.filterBy.toUpperCase()) !== -1) {
            return true;
          }
        }

        return false;
      })

      // Sort items.
      .sort((itemPackageA, itemPackageB) => {
        // Set the default to compare with.
        let a = itemPackageA.position;
        let b = itemPackageB.position;

        switch (this.state.sortBy) {
          case SortBy.Cue:
            a = itemPackageA.item.cue.getSortableText();
            b = itemPackageB.item.cue.getSortableText();
            break;
          case SortBy.Response:
            a = itemPackageA.item.response.getSortableText();
            b = itemPackageB.item.response.getSortableText();
            break;
          case SortBy.PartOfSpeech:
            a = itemPackageA.getPartOfSpeech();
            b = itemPackageB.getPartOfSpeech();
            break;
          default:
            break;
        }

        return a > b ? 1 : (a === b ? 0 : -1);
      });

    return itemPackages.map((itemPackage) => this._renderItem(itemPackage));
  }

  _renderItem(itemPackage) {
    // Some components that might not be rendered.
    let progressBar = null;
    let checkbox = null;

    const memory = this.props.memories.get(itemPackage.getId());
    const className = classnames('item', { skipped: memory && memory.skipped });
    const href = paths.item(itemPackage.getId(), itemPackage.getText());

    if (memory && memory.reviewInterval !== null) {
      progressBar = (<ProgressBar progress={memory.progress} showProgressTag />);
    }

    if (this.props.enableBulkActions) {
      checkbox = (<input type="checkbox" value={itemPackage.getId()} />);
    }

    return (
      <li key={`item-${itemPackage.getId()}`} className={className}>
        <div className="item-details">
          <div className="item-checkbox-container">
            {checkbox}
          </div>

          <div className="item-audio-container">
            {this._renderAudioLink(itemPackage.sound)}
          </div>

          <div className="cue-response">
            <p className="text">
              <a href={href} className="cue">{itemPackage.getText()}</a> {this._renderItemTransliteration(itemPackage)}
            </p>
            <p className="response">{itemPackage.getTranslation()}</p>
          </div>

          <div className="part-of-speech-container">
            <div className="part-of-speech">{partOfSpeech(itemPackage.getPartOfSpeech())}</div>
            {progressBar}
          </div>
        </div>

        <div className="item-sentences">
          {itemPackage.sentences.map(sentence => this._renderSentence(itemPackage, sentence))}
        </div>
      </li>
    );
  }

  _renderAudioLink(sound) {
    if (!sound) {
      return null;
    }

    return (
      <span onClick={() => this._handleAudioClick(sound)} title={t('play audio')} className="glyph glyph-volume-max" />
    );
  }

  _renderSentence(itemPackage, sentence) {
    let sentenceImage = null;

    if (sentence.getImage()) {
      sentenceImage = (
        <a href={sentence.getImage()} data-modal-link="data-modal-link" title={itemPackage.getText()} className="sentence-image">
          <TranscodedImage src={sentence.getImage()} size={TranscodedImageSizes.SMALL} />
        </a>
      );
    }

    return (
      <div key={`sentence-${sentence.getId()}`} className="sentence clearfix">
        {sentenceImage}

        <div className="sentence-audio-container">
          {this._renderAudioLink(sentence.sound)}
        </div>

        <div className="sentence-text">
          <p className="text">
            <MouseoverDictionary html={sentence.getText()} />
          </p>
          <p className="transliteration" dangerouslySetInnerHTML={{ __html: sentence.getTransliteration() || '' }} />
          <p className="translation" dangerouslySetInnerHTML={{ __html: sentence.getTranslation() || '' }} />
        </div>
      </div>
    );
  }

  _renderItemTransliteration(itemPackage) {
    if (!itemPackage.getTransliteration()) {
      return null;
    }

    return (
      <span className="transliteration">[{itemPackage.getTransliteration()}]</span>
    );
  }

  render() {
    const className = classnames('course-content', { waiting: this.props.isLoading });

    return (
      <div ref="courseContent" className={className}>
        <BaseModal
          isOpen={this.state.isAddToCoursesModalOpen}
          onOverlayClick={this._closeAddToCoursesModal}
        >
          <AddToCoursesModal
            goals={this.props.customGoals}
            onClose={this._closeAddToCoursesModal}
            onSubmit={this._submitAddToCoursesModal}
          />
        </BaseModal>

        <header className="content-details">
          <div className="details-primary">
            {this._renderBulkActions()}
          </div>

          <div className="details-secondary">
            <div className="filters">
              <label htmlFor="sort">{t('sort by')}</label>
              {' '}
              <select id="sort" onChange={(...args) => this._handleSortByChange(...args)} value={this.state.sortBy}>
                <option value={SortBy.Default}>{SortBy.Default}</option>
                <option value={SortBy.PartOfSpeech}>{SortBy.PartOfSpeech}</option>
                <option value={SortBy.Cue}>{SortBy.Cue}</option>
                <option value={SortBy.Response}>{SortBy.Response}</option>
              </select>
              {' '}
              <input type="text" name="keyword" className="content-field" value={this.state.filterBy} onChange={(...args) => this._handleFilterByChange(...args)} placeholder={t('filter items')} />
            </div>
          </div>
        </header>

        <ul ref="items" className="items">
          {this._renderItems()}
        </ul>

        <footer className="content-details content-details-footer">
          <div className="details-primary">
            {this._renderBulkActions()}
          </div>
        </footer>
      </div>
    );
  }
}

/**
 * isLoading - prevents actions from being called and indicate loading
 */
CourseContent.propTypes = {
  enableBulkActions: React.PropTypes.bool,
  isLoading: React.PropTypes.bool,
  itemPackages: React.PropTypes.array.isRequired,
  memories: React.PropTypes.instanceOf(Map).isRequired,
  mouseoverDictionary: React.PropTypes.object,
  onAddToCourse: React.PropTypes.func.isRequired,
  customGoals: React.PropTypes.array.isRequired,
  onReset: React.PropTypes.func.isRequired,
  onDefer: React.PropTypes.func.isRequired,
  onSetToComplete: React.PropTypes.func.isRequired,
  onSkip: React.PropTypes.func.isRequired,
  onUnskip: React.PropTypes.func.isRequired,
  isContentCreationEnabled: React.PropTypes.bool,
};

CourseContent.defaultProps = {
  enableBulkActions: true,
  isLoading: false,
  mouseoverDictionary: null,
  isContentCreationEnabled: true,
};
